import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Typography, Stack, Card, CardContent, Grid,
  TextField, MenuItem, Select, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Skeleton, Alert, Button, IconButton,
  Drawer, Divider, InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { submissionsApi } from "../../api/submissions.js";
import type { Submission, SubmissionsResponse } from "../../api/submissions.js";

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>{label}</Typography>
        <Typography variant="h3" sx={{ fontWeight: 700 }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

function intentLabel(intent: string) {
  return intent.replace(/_/g, " ");
}

function parseUserAgent(ua: string): { os: string; browser: string } {
  let os = "Unknown OS";
  if (/windows/i.test(ua))      os = "Windows";
  else if (/iphone|ipad/i.test(ua)) os = "iOS";
  else if (/android/i.test(ua)) os = "Android";
  else if (/mac os/i.test(ua))  os = "macOS";
  else if (/linux/i.test(ua))   os = "Linux";

  let browser = "Unknown Browser";
  if (/edg\//i.test(ua))        browser = "Edge";
  else if (/opr\//i.test(ua))   browser = "Opera";
  else if (/firefox\//i.test(ua)) browser = "Firefox";
  else if (/chrome\//i.test(ua))  browser = "Chrome";
  else if (/safari\//i.test(ua))  browser = "Safari";

  return { os, browser };
}

function exportCsv(submissions: Submission[]) {
  if (submissions.length === 0) return;

  // Collect all variable keys
  const varKeys = Array.from(
    new Set(submissions.flatMap(s => Object.keys(s.variables)))
  );

  const headers = ["Date", "Bot", "Intent", ...varKeys, "Summary", "Session"];
  const rows = submissions.map(s => [
    new Date(s.created_at).toLocaleString(),
    s.bot_name,
    intentLabel(s.trigger_intent),
    ...varKeys.map(k => s.variables[k] ?? ""),
    s.conversation_summary ?? "",
    s.session_id,
  ]);

  const csv = [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `submissions-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="overline"
      display="block"
      sx={{ fontSize: 10, letterSpacing: "0.1em", color: "text.disabled", mb: 1.5 }}
    >
      {children}
    </Typography>
  );
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, textAlign: "right", wordBreak: "break-word" }}>
        {value || "—"}
      </Typography>
    </Stack>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <IconButton size="small" onClick={handleCopy} sx={{ color: copied ? "success.main" : "text.disabled" }}>
      {copied ? <CheckIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
    </IconButton>
  );
}

function copySubmissionData(submission: Submission) {
  const lines: string[] = [
    `Bot: ${submission.bot_name}`,
    `Intent: ${intentLabel(submission.trigger_intent)}`,
    `Date: ${new Date(submission.created_at).toLocaleString()}`,
    "",
    "Collected fields:",
    ...Object.entries(submission.variables).map(([k, v]) => `  ${k.replace(/_/g, " ")}: ${v}`),
  ];
  if (submission.conversation_summary) {
    lines.push("", `Summary: ${submission.conversation_summary}`);
  }
  navigator.clipboard.writeText(lines.join("\n"));
}

function DetailDrawer({ submission, onClose }: { submission: Submission | null; onClose: () => void }) {
  const parsedUA = submission?.user_agent ? parseUserAgent(submission.user_agent) : null;

  return (
    <Drawer
      anchor="right"
      open={!!submission}
      onClose={onClose}
      PaperProps={{ sx: { width: 440, display: "flex", flexDirection: "column" } }}
    >
      {submission && (
        <>
          {/* Scrollable body */}
          <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
            {/* Header */}
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={2}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Submission detail</Typography>
              <IconButton size="small" onClick={onClose} sx={{ mt: -0.5 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            {/* Chips + date */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={1}>
              <Chip label={submission.bot_name} size="small" color="primary" />
              <Chip label={intentLabel(submission.trigger_intent)} size="small" variant="outlined" />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {new Date(submission.created_at).toLocaleString()}
            </Typography>

            <Divider sx={{ my: 2.5 }} />

            {/* Collected fields */}
            <SectionHeader>Collected fields</SectionHeader>
            {Object.entries(submission.variables).length > 0 ? (
              <Stack spacing={1.5} mb={0.5}>
                {Object.entries(submission.variables).map(([k, v]) => (
                  <FieldRow key={k} label={k.replace(/_/g, " ")} value={v} />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" mb={0.5}>No fields collected.</Typography>
            )}

            {/* Conversation summary */}
            {submission.conversation_summary && (
              <>
                <Divider sx={{ my: 2.5 }} />
                <SectionHeader>Conversation summary</SectionHeader>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                  {submission.conversation_summary}
                </Typography>
              </>
            )}

            {/* Session */}
            <Divider sx={{ my: 2.5 }} />
            <SectionHeader>Session</SectionHeader>
            <Stack spacing={1.5}>
              {/* Session ID row */}
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 13, color: "primary.main" }}>
                  {submission.session_id.slice(-8)}
                </Typography>
                <CopyButton text={submission.session_id} />
              </Stack>

              {/* OS + Browser */}
              {parsedUA && (
                <Stack direction="row" spacing={3}>
                  <FieldRow label="OS" value={parsedUA.os} />
                  <FieldRow label="Browser" value={parsedUA.browser} />
                </Stack>
              )}

              {/* Channel + message count */}
              {(submission.session_type || submission.message_count !== undefined) && (
                <Stack direction="row" spacing={3}>
                  {submission.session_type && (
                    <FieldRow
                      label="Channel"
                      value={<Box component="span" sx={{ textTransform: "capitalize" }}>{submission.session_type}</Box>}
                    />
                  )}
                  {submission.message_count !== undefined && (
                    <FieldRow label="Messages" value={submission.message_count} />
                  )}
                </Stack>
              )}

              {/* Origin domain */}
              {submission.origin_domain && (
                <FieldRow label="Origin" value={submission.origin_domain} />
              )}
            </Stack>
          </Box>

          {/* Bottom action bar */}
          <Box sx={{ borderTop: 1, borderColor: "divider", p: 2 }}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon sx={{ fontSize: 15 }} />}
                onClick={() => copySubmissionData(submission)}
                sx={{ flex: 1 }}
              >
                Copy data
              </Button>
            </Stack>
          </Box>
        </>
      )}
    </Drawer>
  );
}

export function SubmissionsPage() {
  const [data, setData]       = useState<SubmissionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  // Filters
  const [search,    setSearch]    = useState("");
  const [botFilter, setBotFilter] = useState("");
  const [intentFilter, setIntentFilter] = useState("");
  const [dateFrom,  setDateFrom]  = useState("");
  const [dateTo,    setDateTo]    = useState("");

  const [selected, setSelected] = useState<Submission | null>(null);

  const load = () => {
    setLoading(true);
    submissionsApi.list({ bot_id: botFilter || undefined, intent: intentFilter || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined })
      .then(setData)
      .catch(() => setError("Failed to load submissions"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [botFilter, intentFilter, dateFrom, dateTo]);

  // Client-side search on top of server-filtered results
  const rows = useMemo(() => {
    if (!data) return [];
    if (!search.trim()) return data.submissions;
    const q = search.toLowerCase();
    return data.submissions.filter(s =>
      Object.values(s.variables).some(v => v.toLowerCase().includes(q)) ||
      s.bot_name.toLowerCase().includes(q) ||
      intentLabel(s.trigger_intent).toLowerCase().includes(q) ||
      (s.conversation_summary ?? "").toLowerCase().includes(q)
    );
  }, [data, search]);

  if (error) return <Alert severity="error">{error}</Alert>;

  const topIntent = data?.by_intent[0]?.intent ?? "—";
  const topBot    = data?.by_bot[0]?.bot_name  ?? "—";

  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h2">Submissions</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Collected data from workflow triggers across all bots.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={() => exportCsv(rows)}
          disabled={rows.length === 0}
        >
          Export CSV
        </Button>
      </Stack>

      {/* Stat cards */}
      <Grid container spacing={2} mb={3} alignItems="stretch">
        <Grid item xs={6} md={3} sx={{ display: "flex" }}>
          {loading ? <Skeleton variant="rounded" height={96} sx={{ flex: 1 }} /> :
            <StatCard label="Total submissions" value={data?.total ?? 0} />}
        </Grid>
        <Grid item xs={6} md={3} sx={{ display: "flex" }}>
          {loading ? <Skeleton variant="rounded" height={96} sx={{ flex: 1 }} /> :
            <StatCard label="This week" value={data?.this_week ?? 0} />}
        </Grid>
        <Grid item xs={6} md={3} sx={{ display: "flex" }}>
          {loading ? <Skeleton variant="rounded" height={96} sx={{ flex: 1 }} /> :
            <StatCard label="Most active bot" value={topBot} sub={data?.by_bot[0] ? `${data.by_bot[0].count} submissions` : undefined} />}
        </Grid>
        <Grid item xs={6} md={3} sx={{ display: "flex" }}>
          {loading ? <Skeleton variant="rounded" height={96} sx={{ flex: 1 }} /> :
            <StatCard label="Top intent" value={intentLabel(topIntent)} sub={data?.by_intent[0] ? `${data.by_intent[0].count} submissions` : undefined} />}
        </Grid>
      </Grid>

      {/* Filters */}
      <Stack direction="row" spacing={1.5} mb={2} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Search…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ width: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Bot</InputLabel>
          <Select label="Bot" value={botFilter} onChange={e => setBotFilter(e.target.value)}>
            <MenuItem value="">All bots</MenuItem>
            {(data?.bots ?? []).map(b => (
              <MenuItem key={b.bot_id} value={b.bot_id}>{b.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Intent</InputLabel>
          <Select label="Intent" value={intentFilter} onChange={e => setIntentFilter(e.target.value)}>
            <MenuItem value="">All intents</MenuItem>
            {(data?.by_intent ?? []).map(i => (
              <MenuItem key={i.intent} value={i.intent}>{intentLabel(i.intent)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField size="small" label="From" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
        <TextField size="small" label="To"   type="date" value={dateTo}   onChange={e => setDateTo(e.target.value)}   InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
        {(botFilter || intentFilter || dateFrom || dateTo) && (
          <Button size="small" color="inherit" onClick={() => { setBotFilter(""); setIntentFilter(""); setDateFrom(""); setDateTo(""); }}>
            Clear filters
          </Button>
        )}
      </Stack>

      {/* Table */}
      {loading ? (
        <Stack spacing={1}>{[1,2,3,4,5].map(i => <Skeleton key={i} variant="rounded" height={52} />)}</Stack>
      ) : rows.length === 0 ? (
        <Box textAlign="center" py={8}>
          <Typography variant="h5" gutterBottom>No submissions yet</Typography>
          <Typography variant="body2" color="text.secondary">
            Submissions will appear here when workflow triggers fire and collect data.
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            {rows.length} {rows.length === 1 ? "submission" : "submissions"}
            {rows.length !== data?.total ? ` (filtered from ${data?.total})` : ""}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Bot</TableCell>
                  <TableCell>Intent</TableCell>
                  <TableCell>Primary value</TableCell>
                  <TableCell>Session</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => {
                  const primaryEntry = Object.entries(row.variables).find(([k]) => k !== "conversation_summary");
                  return (
                    <TableRow
                      key={row.created_at + row.session_id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => setSelected(row)}
                    >
                      <TableCell>
                        <Typography variant="body2">{new Date(row.created_at).toLocaleString()}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={row.bot_name} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip label={intentLabel(row.trigger_intent)} size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
                          {primaryEntry ? primaryEntry[1] : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace", fontSize: 11 }}>
                          {row.session_id.slice(-8)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <DetailDrawer submission={selected} onClose={() => setSelected(null)} />
    </Box>
  );
}
